import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlayerWithExtra, TournamentData } from '@/src/lib/types';
import { fetchPlayers } from '@/src/lib/api';
import { enrichPlayersWithExtraData, formatTeeTime } from '@/src/lib/utils';

interface GroupsAppProps {
  roundId: number;
  apiUrl: string;
}

interface PlayerGroup {
  key: string;
  label: string;
  sortValue: string | number;
  players: PlayerWithExtra[];
}

const parseSortNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
};

const buildGroups = (players: PlayerWithExtra[]): PlayerGroup[] => {
  const visiblePlayers = players.filter(player => Number(player.group_order) !== 0);
  const hasAnyTeeTime = visiblePlayers.some(
    player => typeof player.teetime === 'string' && player.teetime.trim() !== ''
  );

  const grouped = new Map<string, PlayerGroup>();

  visiblePlayers.forEach((player) => {
    if (hasAnyTeeTime) {
      const teeTimeRaw = player.teetime && player.teetime.trim() ? player.teetime : '';
      const groupKey = teeTimeRaw || 'no-tee-time';
      const label = teeTimeRaw ? formatTeeTime(teeTimeRaw) : 'No tee time';

      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          key: groupKey,
          label,
          sortValue: teeTimeRaw || '99:99:99',
          players: [],
        });
      }
      grouped.get(groupKey)!.players.push(player);
      return;
    }

    const groupOrderRaw = player.group_order;
    const hasGroupOrder = groupOrderRaw !== null && groupOrderRaw !== undefined && String(groupOrderRaw).trim() !== '';
    const groupKey = hasGroupOrder ? `group-${groupOrderRaw}` : 'ungrouped';
    const label = hasGroupOrder ? `Group ${groupOrderRaw}` : 'Ungrouped';

    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        key: groupKey,
        label,
        sortValue: hasGroupOrder ? parseSortNumber(groupOrderRaw) : Number.POSITIVE_INFINITY,
        players: [],
      });
    }
    grouped.get(groupKey)!.players.push(player);
  });

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      players: [...group.players].sort((a, b) => {
        const orderA = parseSortNumber(a.group_order);
        const orderB = parseSortNumber(b.group_order);
        if (orderA !== orderB) return orderA - orderB;

        const nameA = `${a.name} ${a.surname}`.toLowerCase();
        const nameB = `${b.name} ${b.surname}`.toLowerCase();
        return nameA.localeCompare(nameB);
      }),
    }))
    .sort((a, b) => {
      if (typeof a.sortValue === 'number' && typeof b.sortValue === 'number') {
        return a.sortValue - b.sortValue;
      }
      return String(a.sortValue).localeCompare(String(b.sortValue), undefined, { numeric: true });
    });
};

export default function GroupsApp({ roundId, apiUrl }: GroupsAppProps) {
  const [players, setPlayers] = useState<PlayerWithExtra[]>([]);
  const [tournament, setTournament] = useState<TournamentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPlayers = useCallback(async () => {
    if (!roundId) return;

    try {
      setLoading(true);
      setError(null);

      const data = await fetchPlayers(roundId, apiUrl);
      if (data.error) {
        setError(data.error);
        return;
      }

      const enrichedPlayers = enrichPlayersWithExtraData(data.players || []);
      setPlayers(enrichedPlayers);
      setTournament(data.tournament || null);
    } catch (err) {
      setError('Failed to load player groups. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [roundId, apiUrl]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const groups = useMemo(() => buildGroups(players), [players]);
  const groupedByTeeTime = useMemo(
    () => players.some(player => typeof player.teetime === 'string' && player.teetime.trim() !== ''),
    [players]
  );

  if (!roundId) {
    return (
      <div className="dolf-manager-widget" style={{ padding: '1rem' }}>
        <div style={{
          background: 'rgba(255,107,53,.08)',
          border: '1px solid rgba(255,107,53,.25)',
          borderRadius: '10px',
          padding: '1rem',
          color: 'var(--color-navy)',
          fontSize: '0.9rem'
        }}>
          data-round attribute is required.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dolf-manager-widget" style={{ padding: '2rem 1rem' }}>
        <div style={{ textAlign: 'center', color: 'var(--color-gray)', fontSize: '0.92rem' }}>
          Loading groups...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dolf-manager-widget" style={{ padding: '1rem' }}>
        <div style={{
          background: 'rgba(220,53,69,.08)',
          border: '1px solid rgba(220,53,69,.25)',
          borderRadius: '10px',
          padding: '1rem',
          color: '#dc3545',
          fontSize: '0.9rem'
        }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dolf-manager-widget groups-root" style={{ padding: '1rem' }}>
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 4mm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }

          .groups-root {
            padding: 0 !important;
            background: transparent !important;
          }

          .dolf-manager-widget {
            background: transparent !important;
          }

          .groups-header {
            margin-bottom: 2.5mm !important;
            padding: 2.5mm 3mm !important;
            background: transparent !important;
          }

          .groups-header,
          .groups-header * {
            color: #000 !important;
          }

          .groups-print-action {
            display: none !important;
          }

          .groups-grid {
            display: grid !important;
            grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
            gap: 1.75mm !important;
          }

          .group-card {
            break-inside: avoid;
            page-break-inside: avoid;
            border-radius: 3mm !important;
            box-shadow: none !important;
          }

          .group-card-header {
            background: #0f1e2e !important;
            border-bottom: 0 !important;
            padding: 1.6mm 2mm !important;
          }

          .group-card-body {
            padding: 1mm 2mm 1.3mm !important;
          }

          .groups-title {
            font-size: 12px !important;
            color: #000 !important;
          }

          .groups-meta {
            font-size: 9px !important;
            color: #000 !important;
          }

          .group-card-title {
            color: #fff !important;
            font-size: 10px !important;
          }

          .group-card-title.print-hide-zero[data-group-label="Group 0"] {
            color: transparent !important;
          }

          .group-card-count {
            color: #0f1e2e !important;
            background: #fff !important;
            border: 0 !important;
            font-size: 9px !important;
            padding: 0.05rem 0.32rem !important;
          }

          .group-player-row {
            gap: 1.6mm !important;
            padding: 1mm 0 !important;
          }

          .group-player-order {
            font-size: 8px !important;
            min-width: 1.1rem !important;
            padding: 0.02rem 0.16rem !important;
          }

          .group-player-order.print-hide-zero[data-order="0"] {
            visibility: hidden !important;
          }

          .group-player-name {
            font-size: 9px !important;
          }

          .group-player-division {
            font-size: 7px !important;
          }

          .group-player-division-inline {
            display: inline !important;
          }

          .group-player-division-block,
          .group-player-status {
            display: none !important;
          }
        }
      `}</style>

      <div className="groups-header" style={{
        background: '#0f1e2e',
        borderRadius: '12px',
        color: '#fff',
        padding: '1rem 1.1rem',
        marginBottom: '0.9rem'
      }}>
        <div className="groups-title" style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.15rem',
          fontWeight: 800,
          letterSpacing: '-0.01em'
        }}>
          {tournament?.tournament || 'Player Groups'}
        </div>
        <div className="groups-meta" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,.8)', marginTop: '0.25rem' }}>
          {tournament?.label ? `${tournament.label} · ` : ''}{players.length} players · {groups.length} groups
        </div>
        <div className="groups-print-action" style={{ marginTop: '0.6rem' }}>
          <button
            onClick={() => window.print()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.38rem 0.7rem',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,.25)',
              background: 'rgba(255,255,255,.1)',
              color: '#fff',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            Print
          </button>
        </div>
      </div>

      <div className="groups-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '0.8rem'
      }}>
        {groups.map((group) => (
          <section
            key={group.key}
            className="group-card"
            style={{
              background: '#fff',
              border: '1.5px solid rgba(14,113,108,.14)',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,.05)'
            }}
          >
            <header
              className="group-card-header"
              style={{
                background: 'rgba(245,247,244,.9)',
                borderBottom: '1px solid rgba(14,113,108,.12)',
                padding: '0.7rem 0.9rem'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem'
              }}>
                <h3
                  className="group-card-title print-hide-zero"
                  style={{
                  margin: 0,
                  fontFamily: 'var(--font-display)',
                  fontSize: '0.95rem',
                  fontWeight: 800,
                  color: 'var(--color-navy)'
                  }}
                  data-group-label={group.label}
                >
                  {group.label}
                </h3>
                <span
                  className="group-card-count"
                  style={{
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    color: 'var(--color-gray)',
                    border: '1px solid rgba(14,113,108,.16)',
                    borderRadius: '999px',
                    padding: '0.08rem 0.45rem',
                    background: '#fff'
                  }}
                >
                  {group.players.length}
                </span>
              </div>
            </header>

            <div className="group-card-body" style={{ padding: '0.45rem 0.9rem 0.6rem' }}>
              {group.players.map((player) => (
                <div
                  key={String(player.player_id)}
                  className="group-player-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    alignItems: 'center',
                    gap: '0.55rem',
                    padding: '0.5rem 0',
                    borderBottom: '1px solid rgba(14,113,108,.08)'
                  }}
                >
                  {groupedByTeeTime ? (
                    <span className="group-player-order print-hide-zero" style={{
                      minWidth: '1.6rem',
                      textAlign: 'center',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: 'var(--color-green)',
                      border: '1px solid rgba(14,113,108,.18)',
                      borderRadius: '6px',
                      padding: '0.1rem 0.28rem',
                      background: 'rgba(245,247,244,.8)'
                    }}
                      data-order={String(player.group_order ?? '')}
                    >
                      {parseSortNumber(player.group_order) === Number.POSITIVE_INFINITY ? '-' : player.group_order}
                    </span>
                  ) : (
                    <span />
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div className="group-player-name" style={{
                      fontSize: '0.88rem',
                      fontWeight: 700,
                      color: 'var(--color-navy)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {player.name} {player.surname}
                      {player.division ? (
                        <span className="group-player-division group-player-division-inline" style={{ display: 'none', fontSize: '0.7rem', color: 'var(--color-gray)', fontWeight: 600 }}>
                          {' · '}{player.division}
                        </span>
                      ) : null}
                    </div>
                    {player.division ? (
                      <div className="group-player-division group-player-division-block" style={{ fontSize: '0.72rem', color: 'var(--color-gray)' }}>
                        {player.division}
                      </div>
                    ) : null}
                  </div>
                  {Number(player.registered) ? (
                    <span className="group-player-status" style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: '#fff',
                      background: 'var(--color-green)',
                      borderRadius: '999px',
                      padding: '0.16rem 0.45rem',
                      whiteSpace: 'nowrap'
                    }}>
                      R
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
